// components/Companies/PerformanceCard.jsx — SOUND-312
// 30-day performance KPI strip + approval-rate sparkline on Company Detail,
// fed by GET /companies/:companyId/performance (presentation layer, SOUND-307/309/311).
// Note: counts come back as approved+declined; the mart's transactions_today is
// inflated (SOUND-314) and intentionally not surfaced by the endpoint.
import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import Skeleton from "components/Skeleton";
import { Activity } from "lucide-react";

const RANGE = 30;

function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/40 ring-1 ring-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

export default function PerformanceCard({ company }) {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const companyId = company?.companyId;

  useEffect(() => {
    if (!companyId || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getCompanyPerformance(companyId, RANGE),
          { id: user.id, accessToken: user.accessToken },
          effectiveCompanyId
        );
        if (!cancelled) setData(res?.data || { points: [], latest: null });
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, effectiveCompanyId]);

  const brand = cssVar("--brand-600", 1) || cssVar("--brand", 1) || "#5affa2";

  return (
    <div className="surface">
      <div className="px-6 py-4 border-b border-border flex items-center space-x-3">
        <div className="p-2 bg-brand/20 rounded-lg">
          <Activity className="w-5 h-5 text-brand" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">30-Day Performance</h2>
      </div>
      <div className="p-6">
        {loading ? (
          <Skeleton.Rect className="h-40 w-full" />
        ) : error ? (
          <p className="text-sm text-muted-foreground">Couldn't load performance data.</p>
        ) : !data?.points?.length ? (
          <p className="text-sm text-muted-foreground">No transaction data available yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Kpi label="Transactions (latest)" value={fmtInt(data.latest?.transactions)} />
              <Kpi label="Approval Rate (latest)" value={fmtPct(data.latest?.approvalRate)} />
              <Kpi label="Revenue (latest)" value={fmtCurrency(data.latest?.revenue)} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                Approval rate · last {RANGE} days
              </p>
              <ResponsiveContainer width="100%" height={80}>
                <LineChart data={data.points} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                  <XAxis dataKey="date" hide />
                  <YAxis domain={[0, 100]} hide />
                  <Tooltip
                    formatter={(v) => [`${Number(v).toFixed(1)}%`, "Approval"]}
                    labelFormatter={(l) => l}
                    contentStyle={{
                      background: cssVar("--card", 0.98),
                      border: `1px solid ${cssVar("--border", 1)}`,
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="approvalRate"
                    stroke={brand}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
